import { Box, Typography } from "@mui/material";
import * as React from "react";
import {
  RichTreeView,
  SimpleTreeView,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import {
  useTreeItem,
  UseTreeItemParameters,
} from "@mui/x-tree-view/useTreeItem";
import {
  TreeItemContent,
  TreeItemIconContainer,
  TreeItemGroupTransition,
  TreeItemRoot,
  TreeItemCheckbox,
} from "@mui/x-tree-view/TreeItem";
import { TreeItemIcon } from "@mui/x-tree-view/TreeItemIcon";
import { TreeItemProvider } from "@mui/x-tree-view/TreeItemProvider";
import { TreeItemDragAndDropOverlay } from "@mui/x-tree-view/TreeItemDragAndDropOverlay";
import Checkbox from "@mui/material/Checkbox";
import { KeyValue, Query, RenderTree } from "../../interfaces";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function sanitize(s: string) {
  return String(s)
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-._/]/g, "");
}

function pickBase(n: RenderTree) {
  return (
    n.variable_ytd ?? n.variable_ytdr ?? n.structureID ?? n.label ?? "node"
  );
}
function ensureIds(
  currentSelectedType: string,
  items: RenderTree[],
  path = "root",
  used = new Set<string>(),
): RenderTree[] {
  const itemsWithIds: RenderTree[] = [];

  items.forEach((item, idx) => {
    if (item.variable_ytdr === "NAMEFULL" || item.variable_ytdr === "TOTAL")
      return;
    const thisPath = `${path}-items[${idx}]`;
    const base = sanitize(pickBase(item));

    const variableName = () => {
      const options: KeyValue = {
        "YTD_$": item.variable_ytd,
        "YTD_%": item.variable_ytdr,
        "Qtly_$": item.variable_qtly,
        "Qtly_%": item.variable_qtlyr,
      };
      return options[currentSelectedType] || null;
    };

    let candidate = variableName() || sanitize(`${thisPath}-${base}`);
    let uniqueId = candidate;
    let counter = 1;
    while (used.has(uniqueId)) {
      uniqueId = `${candidate}__${counter++}`;
    }
    used.add(uniqueId);

    const newItem: RenderTree = {
      ...item,
      id: uniqueId,
      variableName: variableName() ?? uniqueId,
    };

    const children: RenderTree[] = [];

    if (Array.isArray(item.fields)) {
      children.push(
        ...ensureIds(
          currentSelectedType,
          item.fields,
          `${thisPath}/fields`,
          used,
        ),
      );
    }

    if (Array.isArray(item.sub_fields)) {
      children.push(
        ...ensureIds(
          currentSelectedType,
          item.sub_fields,
          `${thisPath}/sub_fields`,
          used,
        ),
      );
    }

    if (children.length) {
      (newItem as any).children = children;
    }

    delete (newItem as any).fields;
    delete (newItem as any).sub_fields;

    itemsWithIds.push(newItem);
  });
  return itemsWithIds;
}

interface CustomTreeItemProps
  extends
    Omit<UseTreeItemParameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {
  apiRef: ReturnType<typeof useTreeViewApiRef>;
  onSecondaryToggle?: (variableName: string, checked: boolean) => void;
  selectedCheckboxes?: string[];
  setSelectedCheckboxes: React.Dispatch<React.SetStateAction<string[]>>;
  includePerformanceConditionRatios: boolean;
  appState: KeyValue;
}

function getAllDescendants(apiRef: any, itemId: string): string[] {
  const result: string[] = [itemId];
  const stack = [itemId];
  while (stack.length) {
    const currentId = stack.pop()!;
    const children =
      apiRef.current?.getItemOrderedChildrenIds?.(currentId) ?? [];
    result.push(...children);
    stack.push(...children);
  }
  return result.filter((str: string) => !str.startsWith("root-"));
}
function getAllDescendantVariableNames(apiRef: any, itemId: string): string[] {
  const ids = getAllDescendants(apiRef, itemId);
  const varNames = ids.map((id) => {
    const item = apiRef.current?.getItem?.(id);
    return (item as any)?.variableName ?? id;
  });
  return Array.from(new Set(varNames));
}

function getItemLevel(apiRef: any, itemId: string): number {
  let level = 0;
  let currentId = itemId;
  while (true) {
    const parentId = apiRef.current?.getParentId?.(currentId);
    if (!parentId) break;
    level++;
    currentId = parentId;
  }
  return level;
}

const CustomTreeItem = React.forwardRef(
  (props: CustomTreeItemProps, ref: React.Ref<HTMLLIElement>) => {
    const {
      apiRef,
      id,
      itemId,
      label,
      disabled,
      children,
      onSecondaryToggle,
      selectedCheckboxes = [],
      setSelectedCheckboxes,
      includePerformanceConditionRatios,
      appState,
      ...other
    } = props;

    const {
      getContextProviderProps,
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const variableName = React.useMemo(() => {
      const item = apiRef.current?.getItem?.(itemId);
      return (item as any)?.variableName ?? itemId;
    }, [apiRef, itemId]);
    const secondaryChecked = selectedCheckboxes.includes(variableName);

    const descendantVarNames = React.useMemo(
      () => getAllDescendantVariableNames(apiRef, itemId),
      [apiRef, itemId],
    );

    const selectedChildrenCount = descendantVarNames.filter((name) =>
      selectedCheckboxes.includes(name),
    ).length;

    const allChildrenSelected =
      descendantVarNames.length > 0 &&
      selectedChildrenCount === descendantVarNames.length;
    const someChildrenSelected =
      selectedChildrenCount > 0 && !allChildrenSelected;

    const selectAllChildren = (varNames: string[], checked: boolean) => {
      const prevSet = new Set(selectedCheckboxes);
      if (checked) {
        varNames.forEach((name) => prevSet.add(name));
      } else {
        varNames.forEach((name) => prevSet.delete(name));
      }
      setSelectedCheckboxes(Array.from(prevSet));
    };

    const labelStr = getLabelProps().children?.toString();
    const level = React.useMemo(
      () => getItemLevel(apiRef, itemId),
      [apiRef, itemId],
    );

    const disablePCR =
      !includePerformanceConditionRatios &&
      itemId.includes("performance-and-condition-ratios");

    const disableDemographicInformation =
      labelStr === "Demographic Information" &&
      appState.ui?.pgcStep === "step3";
    const disableAssetLiabilityCapital =
      itemId.includes("assets-liabilities-and-capital") &&
      appState.ui.incomeBasis[0] === "Qtly";
    return (
      <TreeItemProvider {...getContextProviderProps()}>
        <TreeItemRoot
          {...getRootProps(other)}
          sx={(theme) => ({
            "& *": {
              color: theme.palette.primary.main,
              borderRadius: 1,
              padding: -1,
            },
          })}
        >
          {disablePCR ? (
            <Box
              id="pcr_disabled"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.secondary.main,
                marginX: 1,
                p: 1,
                marginTop: 0.5,
              })}
            >
              <Typography>
                Performance and Condition Ratios:{" "}
                <b>Only Available with Unit Type Percentages</b>
              </Typography>
            </Box>
          ) : disableAssetLiabilityCapital ? (
            <Box
              id="dalc_disabled"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.secondary.main,
                marginX: 1,
                p: 1,
                marginTop: 0.5,
              })}
            >
              <Typography>
                Assets, Liabilities, and Capital:{" "}
                <b>Only Available with Income Basis Year to Date</b>
              </Typography>
            </Box>
          ) : disableDemographicInformation ? (
            <Box
              id="ddi_disabled"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.secondary.main,
                marginX: 1,
                p: 1,
                marginTop: 0.5,
              })}
            >
              <Typography>
                Demographic Information:{" "}
                <b>Not available for Comparison Reports</b>
              </Typography>
            </Box>
          ) : (
            <>
              <TreeItemContent
                {...getContentProps()}
                sx={(theme) => {
                  return {
                    "display": "flex",
                    "alignItems": "center",
                    "pl": 1,
                    "&:hover": {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                    "boxShadow": "none !important",
                    "outline": "none !important",
                    "backgroundColor": "transparent !important",
                  };
                }}
              >
                <Box
                  sx={(theme) => ({
                    backgroundColor:
                      level === 0 ? theme.palette.secondary.main : "white",
                    flexGrow: 1,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    paddingLeft: 1,
                  })}
                >
                  {Array.isArray(children) && children.length > 0 ? (
                    <TreeItemCheckbox
                      {...getCheckboxProps()}
                      id={`${itemId}_all`}
                      checked={allChildrenSelected}
                      indeterminate={someChildrenSelected}
                      onChange={(e, checked) => {
                        e.stopPropagation();
                        selectAllChildren(descendantVarNames, checked);
                      }}
                    />
                  ) : (
                    <Box marginLeft={3} />
                  )}
                  <Box
                    sx={{
                      backgroundColor: (theme) =>
                        Array.isArray(children) && children.length > 0
                          ? theme.palette.secondary.main
                          : "white",
                      borderRadius: 0,
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      ml: level === 0 ? 0 : (level - 1) * 3,
                    }}
                  >
                    {!itemId.startsWith("root-") ? (
                      <Checkbox
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 22,
                          },
                        }}
                        id={`${itemId}_select`}
                        checked={secondaryChecked}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onSecondaryToggle?.(variableName, !secondaryChecked);
                        }}
                      />
                    ) : (
                      <Box height={40} mr={1} />
                    )}
                    <Typography>{labelStr}</Typography>
                    <Box
                      sx={{
                        ml: "auto",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <TreeItemDragAndDropOverlay
                        {...getDragAndDropOverlayProps()}
                      />
                      {!itemId.startsWith("root-") && (
                        <Typography
                          fontSize={16}
                          color="primary.main"
                          sx={{ mr: 8 }}
                        >
                          {variableName}
                        </Typography>
                      )}
                      <TreeItemIconContainer
                        {...getIconContainerProps()}
                        id={`${itemId}_expand`}
                        sx={{
                          "& svg": {
                            fontSize: 30,
                            marginRight: 2,
                          },
                        }}
                      >
                        <TreeItemIcon status={status} />
                      </TreeItemIconContainer>
                    </Box>
                  </Box>
                </Box>
              </TreeItemContent>
              {children && (
                <TreeItemGroupTransition {...getGroupTransitionProps()} />
              )}
            </>
          )}
        </TreeItemRoot>
      </TreeItemProvider>
    );
  },
);

interface FRTreeView {
  reportStructure: RenderTree[];
  appState: Query;
  setSelectedCheckboxes?: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCheckboxes?: string[];
  includePerformanceConditionRatios?: boolean;
  noInstName?: boolean;
  renderTree?: (
    nodes: KeyValue,
    int: number,
    hideInt?: boolean,
    subFootnote?: string,
  ) => React.JSX.Element | undefined;
}

export default function FinancialReportTreeView({
  reportStructure,
  appState,
  setSelectedCheckboxes,
  selectedCheckboxes,
  includePerformanceConditionRatios,
  noInstName,
  renderTree,
}: FRTreeView) {
  const apiRef = useTreeViewApiRef();
  const currentSelectedType = `${appState.ui.incomeBasis[0]}_${appState.ui.unitType[0]}`;

  return appState.ui.reportType === "custom" ? (
    <RichTreeView
      apiRef={apiRef}
      items={
        reportStructure && ensureIds(currentSelectedType, [...reportStructure])
      }
      checkboxSelection
      multiSelect
      selectionPropagation={{
        parents: true,
        descendants: true,
      }}
      slots={{
        item: (props: any) => <CustomTreeItem {...props} appState={appState} />,
        expandIcon: ExpandLessIcon,
        collapseIcon: ExpandMoreIcon,
      }}
      slotProps={{
        item: {
          apiRef,
          onSecondaryToggle: (variableName: string, checked: boolean) => {
            setSelectedCheckboxes &&
              selectedCheckboxes &&
              setSelectedCheckboxes(
                checked
                  ? [...selectedCheckboxes, variableName]
                  : selectedCheckboxes.filter((x: any) => x !== variableName),
              );
          },
          selectedCheckboxes: selectedCheckboxes,
          setSelectedCheckboxes: setSelectedCheckboxes,
          includePerformanceConditionRatios: includePerformanceConditionRatios,
        } as any,
      }}
    />
  ) : (
    <SimpleTreeView
      disableSelection
      className="FRStructure-treeView"
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ExpandMoreIcon,
      }}
      itemChildrenIndentation={0}
    >
      {renderTree &&
        [...reportStructure].map((report) => {
          if (noInstName) {
            delete report.fields[0];
          }
          return report.fields.map((fields: KeyValue, int: number) => {
            return renderTree(fields, int);
          });
        })}
    </SimpleTreeView>
  );
}
